import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TableChartIcon from "@mui/icons-material/TableChart";
import DataObjectIcon from "@mui/icons-material/DataObject";
import productService from "services/product-service";

// Central icon map (use this instead of the inline one below)
const fileIcons = {
    pdf: <PictureAsPdfIcon fontSize="small" color="error" />,
    doc: <DescriptionIcon fontSize="small" color="primary" />,
    docx: <DescriptionIcon fontSize="small" color="primary" />,
    png: <ImageIcon fontSize="small" color="action" />,
    jpg: <ImageIcon fontSize="small" color="action" />,
    jpeg: <ImageIcon fontSize="small" color="action" />,
    csv: <TableChartIcon fontSize="small" color="secondary" />,
    json: <DataObjectIcon fontSize="small" color="info" />,
};

export const getFileIcon = (ext) =>
    fileIcons[ext] || <InsertDriveFileIcon fontSize="small" color="disabled" />;

export default function FilesCard({
    title = "Product Certifications",
    files = [],              // array with { issueDate, expiryDate, key }
    dateFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" },
}) {
    // Normalize date strings
    const normalize = (dateStr) => {
        if (!dateStr) return "-";
        try {
            return new Date(dateStr).toLocaleDateString(undefined, dateFormatOptions);
        } catch {
            return dateStr;
        }
    };

    // Function to get download URL for a file
    const buildDownloadUrl = async (s3Key) => {
        try {
            const response = await productService.getProductFile(s3Key);
            console.log("File download URL:", response);
            const url =
                typeof response === "string"
                    ? response
                    : response?.url || response?.data?.url;

            if (!url) {
                console.warn("No download URL returned.");
                return;
            }

            const a = document.createElement("a");
            a.href = url;
            // Open in same tab (no target _blank)
            const filename = s3Key?.split("/").pop();
            if (filename) a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.log("Error fetching file URL:", error);
        }
    };

    // Parse files data
    const parsedFiles = files.map((c) => {
        const fileName = c.key?.split("/").pop() || c.key || "Unknown";
        // handle double extensions like .csv.pdf -> take last segment
        const rawParts = fileName.toLowerCase().split(".");
        const ext = rawParts.length > 1 ? rawParts[rawParts.length - 1] : "";
        return {
            name: fileName,
            issued: normalize(c.issueDate),
            expires: normalize(c.expiryDate),
            type: ext,
            key: c.key,
        };
    });

    return (
        <Card>
            <MDBox p={3}>
                <MDBox mb={1}>
                    <MDTypography variant="h4" fontWeight="bold" color="text.primary">
                        {title}:
                    </MDTypography>
                </MDBox>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {parsedFiles.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                            No certifications available.
                        </Typography>
                    )}
                    {parsedFiles.map((file, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.1,
                                borderRadius: 2,
                                border: "1px solid #e0e0e0",
                                backgroundColor: "grey.50",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    backgroundColor: "#f0f4ff",
                                    borderColor: "#c5cae9",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                {fileIcons[file.type] || <DescriptionIcon fontSize="small" />}
                                <Box sx={{ lineHeight: 1 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ color: "text.primary", lineHeight: 1.2 }}
                                    >
                                        {file.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.1, lineHeight: 1.2 }}
                                    >
                                        Issued: {file.issued} | Expires: {file.expires}
                                    </Typography>
                                </Box>
                            </Box>

                            <Tooltip title="Download">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    sx={{
                                        backgroundColor: "transparent",
                                        "&:hover": { backgroundColor: "rgba(63,81,181,0.08)" },
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        buildDownloadUrl(file.key)
                                    }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            </MDBox>
        </Card>
    );
}
